import type Graphic from "@arcgis/core/Graphic";
import type Point from "@arcgis/core/geometry/Point";
import type Polyline from "@arcgis/core/geometry/Polyline";
import type Polygon from "@arcgis/core/geometry/Polygon";
import type GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import type PolygonSymbol3D from "@arcgis/core/symbols/PolygonSymbol3D";
import type PointSymbol3D from "@arcgis/core/symbols/PointSymbol3D";
import type LineSymbol3D from "@arcgis/core/symbols/LineSymbol3D";
import type ExtrudeSymbol3DLayer from "@arcgis/core/symbols/ExtrudeSymbol3DLayer";
import type FillSymbol3DLayer from "@arcgis/core/symbols/FillSymbol3DLayer";
import type LineSymbol3DLayer from "@arcgis/core/symbols/LineSymbol3DLayer";
import type ObjectSymbol3DLayer from "@arcgis/core/symbols/ObjectSymbol3DLayer";
import type TextSymbol3DLayer from "@arcgis/core/symbols/TextSymbol3DLayer";
import type SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import type SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import type SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import type TextSymbol from "@arcgis/core/symbols/TextSymbol";
import globalLayers from "./GlobaLMap";
import { baseURL, requestUrl } from "../../baseUrls";
import { loadMultipleModules } from "./LazyloadMap";

// The consumer map comes from the CDN ArcGIS build (esri-loader), so its
// classes must be used here too: a bundled @arcgis/core layer is rejected by
// that Map ("not a Layer"), and its geometries don't autocast either.
const ESRI_MODULES = ['PathSymbol3DLayer', 'Graphic', 'Point', 'Polyline', 'Polygon', 'GraphicsLayer', 'PolygonSymbol3D', 'PointSymbol3D', 'LineSymbol3D', 'ExtrudeSymbol3DLayer', 'FillSymbol3DLayer', 'LineSymbol3DLayer', 'ObjectSymbol3DLayer', 'TextSymbol3DLayer', 'SimpleFillSymbol', 'SimpleLineSymbol', 'SimpleMarkerSymbol', 'TextSymbol'] as const;
let E = {} as Record<typeof ESRI_MODULES[number], any>;
let esriReady: Promise<void> | null = null;
const loadEsri = () => esriReady ??= loadMultipleModules(["esri/symbols/PathSymbol3DLayer", "esri/Graphic", "esri/geometry/Point", "esri/geometry/Polyline", "esri/geometry/Polygon", "esri/layers/GraphicsLayer", "esri/symbols/PolygonSymbol3D", "esri/symbols/PointSymbol3D", "esri/symbols/LineSymbol3D", "esri/symbols/ExtrudeSymbol3DLayer", "esri/symbols/FillSymbol3DLayer", "esri/symbols/LineSymbol3DLayer", "esri/symbols/ObjectSymbol3DLayer", "esri/symbols/TextSymbol3DLayer", "esri/symbols/SimpleFillSymbol", "esri/symbols/SimpleLineSymbol", "esri/symbols/SimpleMarkerSymbol", "esri/symbols/TextSymbol"]).then((classes: any[]) => {
  E = Object.fromEntries(ESRI_MODULES.map((name, i) => [name, classes[i]])) as typeof E;
});

/**
 * Draws the EPC's saved roof design (modules, keepouts, inverters and the
 * other electrical components, string wiring) on the consumer map, the way
 * rooftop3d_epc's projectGraphicsUtils restores it on project load. The same
 * project fields feed a separate set of 3D layers for the SceneView.
 */

const LAYER_PREFIX = "consumer-project-";

type ComponentKind = "INV" | "ACDB" | "DCDB" | "E_AC" | "E_DC" | "LA" | "TP";

// Colors of EPC's component ghosts (MapAreaMapLibre's buildGhostElement).
const COMPONENT_STYLE: Record<ComponentKind, { color: string; width: number; depth: number; height: number }> = {
  INV: { color: "#ffa500", width: 0.7, depth: 0.35, height: 0.9 },
  ACDB: { color: "#f97316", width: 0.6, depth: 0.25, height: 0.7 },
  DCDB: { color: "#2563eb", width: 0.6, depth: 0.25, height: 0.7 },
  E_AC: { color: "#60a5fa", width: 0.35, depth: 0.35, height: 0.35 },
  E_DC: { color: "#38bdf8", width: 0.35, depth: 0.35, height: 0.35 },
  LA: { color: "#ef4444", width: 0.15, depth: 0.15, height: 2.5 },
  TP: { color: "#a855f7", width: 0.4, depth: 0.4, height: 0.4 },
};

// rooftop3d_epc's ThreeSceneLayer materials.
const CONCRETE_COLOR = [201, 203, 205, 1];
const MODULE_GLASS_COLOR = "#0d1f4a";
const MODULE_FRAME_COLOR = "#f1f5f9";
const INVERTER_CABINET_COLOR = "#111827";
const CABLE_CLEARANCE_M = 0.05; // EPC's WIRE_CLEARANCE_M

/** EPC obstructionTypes colour and default height for a keepout name. */
const keepoutLook = (name: string): { color: number[]; height: number } => {
  const n = name.toLowerCase();
  if (n.includes("mumty")) return { color: [239, 68, 68], height: 3 };
  if (n.includes("column")) return { color: [220, 38, 38], height: 1 };
  if (n.includes("tank")) return { color: [132, 204, 22], height: 3 };
  if (n.includes("tree")) return { color: [132, 204, 22], height: 6 };
  if (n.includes("parapet") || n.includes("handrail")) return { color: [107, 114, 128], height: 1 };
  if (n.includes("walkway")) return { color: [59, 130, 246], height: 0.03 };
  if (n.includes("hvac") || n.includes("vent") || n.includes("skylight")) return { color: [245, 158, 11], height: 1 };
  if (n.includes("chimney")) return { color: [255, 0, 0], height: 5 };
  return { color: [120, 113, 108], height: 1 };
};

/** EPC stringingCables materials per stringing role; null = not drawn in 3D. */
const cableLook = (role: string): { color: string; size?: number; path?: "quad" | "circle"; width?: number; height?: number } | null => {
  switch (role) {
    case "wires":
    case "leads":
      return { color: "#16181c", size: 1.2 };
    case "trunk":
      return { color: "#a8adb4", path: "quad", width: 0.12, height: 0.05 };
    case "terminationLines":
      return { color: "#8b9099", path: "circle", width: 0.044, height: 0.044 };
    default:
      // Legacy ArcGIS-engine saves carry no role: draw them as plain cable.
      return role ? null : { color: "#16181c", size: 1.2 };
  }
};

// EPC's module look (Colormap.pvmoduleSurface / pvmoduleOutline): navy cells, white frame.
const PANEL_COLOR = [27, 59, 104, 0.92];
const PANEL_EDGE = [255, 255, 255, 1];
const KEEPOUT_COLOR = [239, 68, 68, 0.32];
const KEEPOUT_EDGE = [185, 28, 28, 0.95];
const DEFAULT_ROOF_HEIGHT = 3;

const parse = (value: unknown): any => {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return null; }
};

const toArcGeometry = (json: any): Point | Polyline | Polygon | null => {
  if (!json) return null;
  const spatialReference = json.spatialReference ?? { wkid: 4326 };
  if (Array.isArray(json.rings)) {
    const hasZ = json.hasZ ?? json.rings.some((ring: number[][]) => ring?.some((p) => p?.length > 2));
    return new E.Polygon({ rings: json.rings, spatialReference, hasZ });
  }
  if (Array.isArray(json.paths)) {
    const hasZ = json.hasZ ?? json.paths.some((path: number[][]) => path?.some((p) => p?.length > 2));
    return new E.Polyline({ paths: json.paths, spatialReference, hasZ });
  }
  if (typeof json.x === "number" && typeof json.y === "number") {
    return new E.Point({ x: json.x, y: json.y, z: json.z, spatialReference });
  }
  return null;
};

/** Esri-JSON graphics saved as a JSON string, an array, `{graphics: [...]}` or a single graphic. */
const esriGraphics = (value: unknown, keepSymbol = false): Graphic[] => {
  const parsed = parse(value);
  const rows = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.graphics) ? parsed.graphics : parsed?.geometry ? [parsed] : [];
  return rows.flatMap((row: any) => {
    const item = parse(row);
    try {
      const geometry = toArcGeometry(item?.geometry);
      if (!geometry) return [];
      const graphic = new E.Graphic({ geometry, attributes: { ...(item.attributes ?? {}) } });
      if (keepSymbol && item.symbol && Object.keys(item.symbol).length) {
        graphic.symbol = E.Graphic.fromJSON({ symbol: item.symbol }).symbol;
      }
      return [graphic];
    } catch { return []; }
  });
};

const geoJsonGeometry = (geometry: any): Point | Polyline | Polygon | null => {
  const spatialReference = { wkid: 4326 };
  const coords = geometry?.coordinates;
  if (!coords) return null;
  switch (geometry.type) {
    case "Point":
      return typeof coords[0] === "number" && typeof coords[1] === "number"
        ? new E.Point({ longitude: coords[0], latitude: coords[1], z: coords[2], spatialReference })
        : null;
    case "LineString": return new E.Polyline({ paths: [coords], spatialReference });
    case "MultiLineString": return new E.Polyline({ paths: coords, spatialReference });
    case "Polygon": return new E.Polygon({ rings: coords, spatialReference });
    case "MultiPolygon": return new E.Polygon({ rings: coords.flat(), spatialReference });
    default: return null;
  }
};

const geoJsonGraphics = (value: unknown): Graphic[] => {
  const parsed = parse(value);
  const features = parsed?.type === "FeatureCollection" ? parsed.features : parsed?.type === "Feature" ? [parsed] : null;
  if (!Array.isArray(features)) return [];
  return features.flatMap((feature: any) => {
    const geometry = geoJsonGeometry(feature?.geometry);
    return geometry ? [new E.Graphic({ geometry, attributes: { ...(feature.properties ?? {}) } })] : [];
  });
};

const anchorPoint = (geometry: __esri.Geometry | null | undefined): Point | null => {
  if (!geometry) return null;
  if (geometry.type === "point") return geometry as Point;
  if (geometry.type === "polygon") return (geometry as Polygon).centroid ?? geometry.extent?.center ?? null;
  return geometry.extent?.center ?? null;
};

const flatten = (geometry: Polygon): Polygon =>
  new E.Polygon({
    rings: geometry.rings.map((ring) => ring.map((p) => [p[0], p[1]])),
    spatialReference: geometry.spatialReference,
  });

// ---------------------------------------------------------------------------
// Reading the saved design
// ---------------------------------------------------------------------------

interface ProjectDesign {
  panels: Graphic[];
  keepouts: Graphic[];
  components: { kind: ComponentKind; label: string; point: Point; cabinet?: { width: number; depth: number; height: number; heading: number } }[];
  wiring: { id: string; role: string; title: string; opacity: number; graphics: Graphic[]; geojson: boolean }[];
  roofHeight: number;
  panelTop: number;
}

const satelliteKind = (lowerName: string): ComponentKind | null => {
  if (/^acdb(-\d+)?$/.test(lowerName)) return "ACDB";
  if (/^dcdb(-\d+)?$/.test(lowerName)) return "DCDB";
  if (!["term", "earth", "arr", "lightning", "e_ac", "e_dc"].some((s) => lowerName.includes(s))) return null;
  // Same naming rule as EPC's addJSONGraphicstoSketch.
  return lowerName.includes("ter") ? "TP" : lowerName.includes("ac") ? "E_AC" : lowerName.includes("dc") ? "E_DC" : "LA";
};

const readDesign = (project: any, row = 0): ProjectDesign => {
  const panels = esriGraphics(project?.panelLayer);
  const keepouts: Graphic[] = [];
  const components: ProjectDesign["components"] = [];

  const savedKeepouts = parse(project?.keepouts);
  (Array.isArray(savedKeepouts) ? savedKeepouts : []).forEach((item: any) => {
    const graphicValue = parse(item?.graphic);
    const graphics = geoJsonGraphics(graphicValue);
    (graphics.length ? graphics : esriGraphics(graphicValue)).forEach((graphic) => {
      const name = String(item?.name ?? graphic.attributes?.name ?? "");
      const lowerName = name.toLowerCase();
      // Inverters are restored from inverterplacementdata below.
      if (lowerName.includes("inv")) return;
      const kind = satelliteKind(lowerName);
      if (kind) {
        const point = anchorPoint(graphic.geometry);
        if (point) components.push({ kind, label: kind === "ACDB" || kind === "DCDB" ? name.toUpperCase() : kind, point });
        return;
      }
      if (graphic.geometry?.type !== "polygon") return;
      graphic.attributes = { ...graphic.attributes, name, infraType: item?.infraType };
      keepouts.push(graphic);
    });
  });

  const placement = parse(project?.inverterplacementdata);
  const inverterList = placement?.inverterPlacementDetails?.invertergraphicList;
  (Array.isArray(inverterList) ? inverterList : []).forEach((item: unknown, index: number) => {
    esriGraphics(item).forEach((graphic) => {
      const point = anchorPoint(graphic.geometry);
      if (!point) return;
      const attrs = graphic.attributes ?? {};
      const inverterIndex = Number(attrs.inverterIndex);
      const label = attrs.inverterLabel || attrs.sharedInverterName
        || `INV-${(Number.isFinite(inverterIndex) ? inverterIndex : index) + 1}`;
      // EPC saves each inverter cabinet's real size and roof-row bearing.
      const width = Number(attrs.invWidthM), depth = Number(attrs.invDepthM), height = Number(attrs.invHeightM);
      const cabinet = width > 0 && depth > 0 && height > 0
        ? { width, depth, height, heading: Number(attrs.invBearingDeg) || 0 }
        : undefined;
      components.push({ kind: "INV", label: String(label), point, cabinet });
    });
  });

  const wiring: ProjectDesign["wiring"] = [];
  const stringing = parse(parse(project?.stringingdata)?.serializeAutoStringing);
  (Array.isArray(stringing) ? stringing : []).forEach((layer: any, index: number) => {
    // MapLibre saves {role, geojson, entry}; the ArcGIS engine saved {id, title, graphics}.
    const isGeoJson = Boolean(layer?.geojson);
    const graphics = isGeoJson ? geoJsonGraphics(layer.geojson) : esriGraphics(layer?.graphics, true);
    if (!graphics.length) return;
    const entry = layer?.entry ?? layer;
    if (String(entry?.title ?? "").startsWith("autostringing_,visibl")) return;
    wiring.push({
      id: `${LAYER_PREFIX}wiring-${row}-${index}`,
      role: String(layer?.role ?? ""),
      title: entry?.title || `Stringing ${layer?.role ?? index + 1}`,
      opacity: Number.isFinite(entry?.opacity) && entry.opacity > 0 ? entry.opacity : 1,
      graphics,
      geojson: isGeoJson,
    });
  });

  // EPC stores each module ring's real height above ground (roof + tilt).
  const panelZ = panels.flatMap((panel) => {
    const rings = (panel.geometry as Polygon)?.rings ?? [];
    const z = rings.flat().map((p) => Number(p[2])).filter((n) => Number.isFinite(n) && n > 0);
    const base = Number(panel.attributes?.base);
    const top = Number(panel.attributes?.top);
    return [...z, ...(base > 0 ? [base] : []), ...(top > 0 ? [top] : [])];
  });
  const minPanelZ = panelZ.length ? Math.min(...panelZ) : NaN;
  const buildingHeight = Number(project?.buildingheight);
  let roofHeight = DEFAULT_ROOF_HEIGHT;
  if (buildingHeight > 0 && (!Number.isFinite(minPanelZ) || buildingHeight <= minPanelZ)) roofHeight = buildingHeight;
  else if (Number.isFinite(minPanelZ)) roofHeight = Math.max(0.5, minPanelZ - 0.15);
  const panelTop = panelZ.length ? Math.max(...panelZ) : roofHeight + 0.3;

  return { panels, keepouts, components, wiring, roofHeight, panelTop };
};

// ---------------------------------------------------------------------------
// 2D (MapView) layers
// ---------------------------------------------------------------------------

const panelSymbol2d = () => new E.SimpleFillSymbol({
  color: PANEL_COLOR as any,
  outline: { color: PANEL_EDGE as any, width: 0.75 },
});

// The wiring overlay also puts sequence numbers and connector dots on every
// module; EPC's look keeps the modules clean, so only the wire runs are drawn.
const isWiringLabel = (graphic: Graphic) => graphic.geometry?.type === "point";

const wiringSymbol2d = (graphic: Graphic): any => {
  const p = graphic.attributes ?? {};
  const type = graphic.geometry?.type;
  if (type === "point" && (p.text || p.label) && (p.fontSize || p.haloColor)) {
    return new E.TextSymbol({
      text: p.text ?? p.label,
      color: p.color ?? "#172554",
      haloColor: p.haloColor ?? "#ffffff",
      haloSize: 1.5,
      font: { size: p.fontSize ?? 10, weight: "bold" },
    });
  }
  if (type === "point") {
    return new E.SimpleMarkerSymbol({
      style: "circle",
      color: p.color ?? "#f97316",
      size: Math.max(5, Number(p.radius ?? 3) * 2),
      outline: { color: p.outlineColor ?? "#ffffff", width: 1 },
    });
  }
  if (type === "polygon") {
    return new E.SimpleFillSymbol({ color: p.color ?? KEEPOUT_COLOR, outline: { color: p.outlineColor ?? KEEPOUT_EDGE, width: 1 } });
  }
  return new E.SimpleLineSymbol({
    style: p.dashed ? "dash" : "solid",
    color: p.color ?? "#2563eb",
    width: Math.max(1, Number(p.width ?? 2)),
  });
};

const componentGraphics2d = (components: ProjectDesign["components"]) =>
  components.flatMap(({ kind, label, point }) => [
    new E.Graphic({
      geometry: point,
      attributes: { name: label, componentType: kind },
      symbol: new E.SimpleMarkerSymbol({ style: "square", color: COMPONENT_STYLE[kind].color, size: 11, outline: { color: "#111827", width: 1 } }),
    }),
    new E.Graphic({
      geometry: point,
      attributes: { name: `${label}-label`, componentType: kind },
      symbol: new E.TextSymbol({
        text: label,
        color: "#ffffff",
        backgroundColor: [30, 64, 175, 0.95],
        font: { size: 9, weight: "bold" },
        yoffset: 12,
      } as any),
    }),
  ]);

const replaceLayer = (id: string, title: string, graphics: Graphic[], opacity = 1) => {
  const map = globalLayers.map;
  if (!map) return false;
  const old = map.findLayerById(id);
  if (old) map.remove(old);
  if (graphics.length === 0) return false;
  map.add(new E.GraphicsLayer({ id, title, graphics, opacity }));
  return true;
};

// ---------------------------------------------------------------------------
// The building's roof rows
// ---------------------------------------------------------------------------

// EPC keeps one project row per roof (GetProjectsByBuildingid); the consumer's
// own row is only the roof it first drew, so the EPC's redrawn roofs and their
// designs live in the building's rows.
let buildingRoofs: { buildingid: string; rows: any[] } | null = null;

const fetchBuildingRoofs = async (project: any): Promise<any[] | null> => {
  const buildingid = project?.buildingid;
  if (!buildingid) return null;
  try {
    const { data } = await baseURL.get(`${requestUrl.projectByBuild}/${buildingid}`);
    if (data?.code !== "200") return null;
    const rows = parse(data.responseData)?.projects;
    return Array.isArray(rows) && rows.length ? rows : null;
  } catch (error) {
    console.warn("Could not load the building's roofs; showing the project's own roof only.", error);
    return null;
  }
};

const rowsFor = (project: any): any[] =>
  buildingRoofs && project?.buildingid && buildingRoofs.buildingid === project.buildingid ? buildingRoofs.rows : [project];

/** A roof row's saved outline (Esri JSON graphic, `{features}` or GeoJSON), without Z. */
const rowRoofGeometry = (row: any): Polygon | null => {
  const saved = parse(row?.graphicLayer);
  if (!saved) return null;
  const fromGeoJson = geoJsonGraphics(saved).find((g) => g.geometry?.type === "polygon");
  const geometry = fromGeoJson?.geometry ?? toArcGeometry(saved.geometry ?? saved.features?.[0]?.geometry);
  return geometry?.type === "polygon" ? flatten(geometry as Polygon) : null;
};

const ownRoofGraphic = () => globalLayers.sketchLayers?.graphics
  .toArray()
  .find((graphic) => graphic.geometry?.type === "polygon" && graphic.attributes?.name === "building")
  ?? globalLayers.sketchLayers?.graphics.toArray().find((graphic) => graphic.geometry?.type === "polygon");

const drawRows = (rows: any[], showRoofOutlines: boolean) => {
  const map = globalLayers.map;
  if (!map) return false;
  map.layers
    .filter((layer) => layer.id?.startsWith(LAYER_PREFIX))
    .toArray()
    .forEach((layer) => map.remove(layer));

  const roofs: Graphic[] = [];
  const panels: Graphic[] = [];
  const keepouts: Graphic[] = [];
  const components: ProjectDesign["components"] = [];
  const wiring: ProjectDesign["wiring"] = [];
  rows.forEach((row, index) => {
    const design = readDesign(row, index);
    const roof = showRoofOutlines ? rowRoofGeometry(row) : null;
    if (roof) {
      roofs.push(new E.Graphic({
        geometry: roof,
        attributes: { name: `roof-${index}` },
        symbol: new E.SimpleFillSymbol({ color: [173, 150, 50, 0.5], outline: { color: [150, 55, 0], width: 1.5 } }),
      }));
    }
    design.panels.forEach((panel) => { panel.symbol = panelSymbol2d(); });
    design.keepouts.forEach((keepout) => {
      keepout.symbol = new E.SimpleFillSymbol({ color: KEEPOUT_COLOR as any, outline: { color: KEEPOUT_EDGE as any, width: 1.2 } });
    });
    design.wiring.forEach((layer) => {
      layer.graphics = layer.graphics.filter((graphic) => !isWiringLabel(graphic));
      layer.graphics.forEach((graphic) => {
        if (layer.geojson || !graphic.symbol) graphic.symbol = wiringSymbol2d(graphic);
      });
    });
    panels.push(...design.panels);
    keepouts.push(...design.keepouts);
    components.push(...design.components);
    wiring.push(...design.wiring);
  });
  // Added bottom to top: roofs, keepouts, modules, wiring, components.
  let drawn = replaceLayer(`${LAYER_PREFIX}roofs`, "Project Roofs", roofs);
  drawn = replaceLayer(`${LAYER_PREFIX}keepouts`, "Project Keepouts", keepouts) || drawn;
  drawn = replaceLayer(`${LAYER_PREFIX}modules`, "Project Modules", panels) || drawn;
  wiring.forEach((layer) => { drawn = replaceLayer(layer.id, layer.title, layer.graphics, layer.opacity) || drawn; });
  drawn = replaceLayer(`${LAYER_PREFIX}electrical`, "Project Electrical Components", componentGraphics2d(components)) || drawn;
  return drawn;
};

/**
 * Draw the EPC's saved layout on the consumer's 2D map: the project's own row
 * right away, then every roof of its building once those rows arrive.
 */
export const restoreProjectRoofOverlays = async (project: any) => {
  try {
    await loadEsri();
  } catch (error) {
    console.error("Could not load the map classes for the project design.", error);
    return;
  }
  if (!buildingRoofs || buildingRoofs.buildingid !== project?.buildingid) buildingRoofs = null;
  drawRows(rowsFor(project), Boolean(buildingRoofs));
  void fetchBuildingRoofs(project).then((rows) => {
    // Ignore a late answer for a project that is no longer on the map.
    if (!rows || globalLayers.currentSelectedProject?.[0]?.projectid !== project?.projectid) return;
    const withRoofs = rows.filter((row) => rowRoofGeometry(row));
    if (!withRoofs.length) return;
    buildingRoofs = { buildingid: project.buildingid, rows: withRoofs };
    // The building's rows carry the current roofs, so they replace the
    // outline the consumer drew originally.
    const ownRoof = ownRoofGraphic();
    if (ownRoof) ownRoof.visible = false;
    drawRows(withRoofs, true);
  });
};

// ---------------------------------------------------------------------------
// 3D (SceneView) layers
// ---------------------------------------------------------------------------

// Every 3D layer is placed at an absolute height above ONE ground sample
// taken at the roof (anchorScene3dLayers), so the building, keepouts, modules,
// cabling and equipment share the same base and nothing floats above the roof
// the way per-feature "relative-to-ground" placement on uneven terrain did.
const layer3d = (id: string, title: string, graphics: Graphic[], _mode: "on-the-ground" | "relative-to-ground", offset = 0) => {
  const layer = new E.GraphicsLayer({
    id,
    title,
    graphics,
    elevationInfo: { mode: "absolute-height", offset },
  });
  (layer as any).__heightAboveGround = offset;
  return layer;
};

/** Lift the 3D layers onto the ground height measured at the roof. */
export const anchorScene3dLayers = (layers: GraphicsLayer[], groundZ: number) => {
  layers.forEach((layer) => {
    const above = (layer as any).__heightAboveGround;
    if (typeof above === "number") layer.elevationInfo = { mode: "absolute-height", offset: above + groundZ } as any;
  });
};

/** Layers for the consumer's 3D preview: every roof's mass, modules, keepouts, electrical components and wiring. */
export const buildProjectScene3dLayers = async (project: any): Promise<GraphicsLayer[]> => {
  await loadEsri();
  const rows = rowsFor(project);
  const usingOwnRow = rows.length === 1 && rows[0] === project;
  return rows.flatMap((row, index) => {
    const ownRoof = usingOwnRow ? ownRoofGraphic()?.geometry : null;
    return buildRoofScene3dLayers(row, index, ownRoof ? flatten(ownRoof as Polygon) : rowRoofGeometry(row));
  });
};

const buildRoofScene3dLayers = (project: any, row: number, roof: Polygon | null): GraphicsLayer[] => {
  const design = readDesign(project, row);
  const { roofHeight } = design;
  const layers: GraphicsLayer[] = [];
  const id = (name: string) => `${LAYER_PREFIX}3d-${name}-${row}`;

  if (roof) {
    // EPC's 3D building: one concrete prism from the ground to the roof.
    layers.push(layer3d(id("roof"), "Roof", [new E.Graphic({
      geometry: roof,
      symbol: new E.PolygonSymbol3D({
        symbolLayers: [new E.ExtrudeSymbol3DLayer({
          size: roofHeight,
          material: { color: CONCRETE_COLOR },
          edges: { type: "solid", color: [110, 114, 120, 0.7], size: 0.6 } as any,
        })],
      }),
    })], "on-the-ground"));
  }
  if (design.keepouts.length) {
    layers.push(layer3d(id("keepouts"), "Keepouts", design.keepouts.map((keepout) => {
      const look = keepoutLook(String(keepout.attributes?.name ?? keepout.attributes?.infraType ?? ""));
      const height = Number(keepout.attributes?.height ?? keepout.attributes?.infraheight);
      return new E.Graphic({
        geometry: flatten(keepout.geometry as Polygon),
        attributes: keepout.attributes,
        symbol: new E.PolygonSymbol3D({
          symbolLayers: [new E.ExtrudeSymbol3DLayer({
            size: height > 0 ? height : look.height,
            material: { color: look.color },
          })],
        }),
      });
    }), "relative-to-ground", roofHeight));
  }

  if (design.panels.length) {
    const hasHeight = (panel: Graphic) => (panel.geometry as Polygon).rings.some((ring) => ring.some((p) => Number(p[2]) > 0));
    // Module rings keep their saved Z, so each module sits tilted at its real height.
    const tiltedPanels = design.panels.filter(hasHeight);
    const flatPanels = design.panels.filter((panel) => !hasHeight(panel));
    if (tiltedPanels.length) {
      layers.push(layer3d(id("modules"), "Modules", tiltedPanels.map((panel) => new E.Graphic({
        geometry: panel.geometry,
        attributes: panel.attributes,
        symbol: new E.PolygonSymbol3D({
          symbolLayers: [new E.FillSymbol3DLayer({
            material: { color: MODULE_GLASS_COLOR },
            outline: { color: MODULE_FRAME_COLOR, size: 0.8 },
          })],
        }),
      })), "relative-to-ground"));
    }
    if (flatPanels.length) {
      layers.push(layer3d(id("modules-flat"), "Modules", flatPanels.map((panel) => new E.Graphic({
        geometry: flatten(panel.geometry as Polygon),
        symbol: new E.PolygonSymbol3D({
          symbolLayers: [new E.ExtrudeSymbol3DLayer({ size: 0.06, material: { color: MODULE_GLASS_COLOR }, edges: { type: "solid", color: MODULE_FRAME_COLOR, size: 0.6 } as any })],
        }),
      })), "relative-to-ground", roofHeight + 0.3));
    }
  }

  // EPC runs the cabling just above the roof surface, under the modules (so
  // they hide it, as on a real roof): black PV cable, galvanised trays for the
  // trunks and grey conduit for the AC run to the termination point.
  design.wiring.forEach((wiring) => {
    const look = cableLook(wiring.role);
    if (!look) return;
    const graphics = wiring.graphics
      .filter((graphic) => graphic.geometry?.type === "polyline")
      .map((graphic) => new E.Graphic({
        geometry: graphic.geometry,
        attributes: graphic.attributes,
        symbol: new E.LineSymbol3D({
          symbolLayers: [look.path
            ? new E.PathSymbol3DLayer({ profile: look.path, width: look.width, height: look.height, material: { color: look.color } })
            : new E.LineSymbol3DLayer({ size: look.size, material: { color: look.color } })],
        }),
      }));
    if (graphics.length) layers.push(layer3d(`${wiring.id}-3d`, wiring.title, graphics, "relative-to-ground", roofHeight + CABLE_CLEARANCE_M));
  });

  if (design.components.length) {
    const boxes = design.components.map(({ kind, label, point, cabinet }) => {
      const style = COMPONENT_STYLE[kind];
      const size = cabinet ?? { width: style.width, depth: style.depth, height: style.height, heading: 0 };
      return new E.Graphic({
        geometry: new E.Point({ x: point.x, y: point.y, spatialReference: point.spatialReference }),
        attributes: { name: label, componentType: kind },
        symbol: new E.PointSymbol3D({
          symbolLayers: [new E.ObjectSymbol3DLayer({
            resource: { primitive: kind === "LA" ? "cylinder" : "cube" },
            width: size.width,
            depth: size.depth,
            height: size.height,
            heading: size.heading,
            anchor: "bottom",
            material: { color: kind === "INV" ? INVERTER_CABINET_COLOR : style.color },
          })],
        }),
      });
    });
    layers.push(layer3d(id("electrical"), "Electrical Components", boxes, "relative-to-ground", roofHeight));

    const labels = design.components.map(({ kind, label, point }) => new E.Graphic({
      geometry: new E.Point({ x: point.x, y: point.y, spatialReference: point.spatialReference }),
      symbol: new E.PointSymbol3D({
        symbolLayers: [new E.TextSymbol3DLayer({
          text: label,
          size: 10,
          material: { color: "#ffffff" },
          background: { color: [30, 64, 175, 0.95] },
          font: { weight: "bold" },
        } as any)],
        verticalOffset: { screenLength: 18 },
        callout: { type: "line", size: 1, color: [30, 64, 175, 1] },
      } as any),
      attributes: { componentType: kind },
    }));
    layers.push(layer3d(id("electrical-labels"), "Electrical Labels", labels, "relative-to-ground",
      roofHeight + Math.max(...design.components.map(({ kind }) => COMPONENT_STYLE[kind].height))));
  }

  return layers;
};
